import { AppSidebar } from "@/app/dashboard/app-sidebar"
import AccountTab from "@/domains/account/components"
import AIMetricsTab from "@/domains/admin/components/AIMetricsTab"
import AttributionTab from "@/domains/admin/components/AttributionTab"
import SubscriptionsTab from "@/domains/admin/components/SubscriptionsTab"
import UsageMetricsTab from "@/domains/admin/components/UsageMetricsTab"
import { useIsAdmin } from "@/domains/admin/hooks/useIsAdmin"
import PlansTab from "@/domains/billing/components/PlansTab"
import ConnectorTab from "@/domains/market-pulse/components/ConnectorTab"
import LiveOptionsTab from "@/domains/market-pulse/components/LiveOptionsTab"
import Overview from "@/domains/market-pulse/components/Overview"
import ZeroDTETab from "@/domains/market-pulse/components/ZeroDTETab"
import NotificationsTab from "@/domains/notifications/components/NotificationsTab"
import { HelloWorldTask } from "@/domains/tasks-demo/components/HelloWorldTask"
import { EditionPage } from "@/domains/trade-ideas/pages/EditionPage"
import { LedgerPage } from "@/domains/trade-ideas/pages/LedgerPage"
import TradeIdeasPage from "@/domains/trade-ideas/pages/TradeIdeasPage"
import WatchesTab from "@/domains/watches/components/WatchesTab"
import WatchCreatePage from "@/domains/watches/pages/WatchCreatePage"
import { ErrorBoundary } from "@/shared/components/ErrorBoundary"
import { PortalPageSkeleton } from "@/shared/components/PortalPageSkeletons"
import { getTimeFormat, getTimezone } from "@/shared/lib/timezone"
import type { ApiFn, Entitlements, Profile } from "@/shared/lib/types"
import { Button } from "@/shared/ui/button"
import { T } from "@/shared/ui/DesignLib"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog"
import { Separator } from "@/shared/ui/separator"
import { SidebarProvider, SidebarTrigger, useSidebar } from "@/shared/ui/sidebar"
import React, { useEffect, useRef, useState } from "react"

interface DashboardProps {
  tabId: string
  profile: Profile | null
  entitlements: Entitlements | null
  loading?: boolean
  onUpgrade: () => void
  api: ApiFn
  onNewWatch?: () => void
  onTabChange: (tabId: string) => void
  /** JS-1094: the daily edition's dated URL segment (`/portal/trade-ideas/edition/:asOf`),
   * threaded through so EditionPortalPage can keep the portal shell around the
   * edition surface instead of rendering it bare. `null`/undefined means "today". */
  editionAsOf?: string | null
  /** JS-1094: called when the reader picks a different edition from the archive
   * rail -- the caller (EditionPortalPage) owns the dated URL. */
  onSelectEditionAsOf?: (asOf: string) => void
}

function TrialExpiredDialog({ onUpgrade }: { onUpgrade: () => void }) {
  const [open, setOpen] = useState(true)

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle className="text-center text-lg font-bold">Trial Expired</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-center text-sm">
            Your free trial has ended. Your dashboard and watches are locked until you upgrade.
          </p>
          <Button
            className="w-full"
            style={{ backgroundColor: T.primary, color: "white" }}
            onClick={() => {
              setOpen(false)
              onUpgrade()
            }}
          >
            Upgrade Now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function MobileTabSync({
  tabId,
  isMobile,
  mobileMainRef,
}: {
  tabId: string
  isMobile: boolean
  mobileMainRef: React.RefObject<HTMLDivElement | null>
}) {
  const { setOpenMobile } = useSidebar()

  useEffect(() => {
    if (!isMobile) return

    setOpenMobile(false)
    mobileMainRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" })
    window.scrollTo({ top: 0, left: 0, behavior: "auto" })
  }, [isMobile, tabId, mobileMainRef, setOpenMobile])

  return null
}

export function Dashboard({
  tabId,
  profile,
  entitlements,
  loading = false,
  onUpgrade,
  api,
  onNewWatch,
  onTabChange,
  editionAsOf,
  onSelectEditionAsOf,
}: DashboardProps) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024)
  const [estTime, setEstTime] = useState<string>("");
  const mobileMainRef = useRef<HTMLDivElement | null>(null);
  const isToolboxPlan = (profile?.plan || "").toLowerCase() === "toolbox";
  const { isAdmin, loading: adminCheckLoading } = useIsAdmin(Boolean(profile));
  // Get page title based on active tab
  const getPageTitle = () => {
    const titles: { [key: string]: string } = {
      overview: "Overview",
      connector: "Connector",
      watches: "Watches",
      "trade-ideas": "Trade Ideas",
      "trade-ideas-edition": "Daily Edition",
      "trade-ideas-ledger": "Outcome Ledger",
      "watches-create": "New Watch",
      account: "Account",
      notifications: "Notification Preferences",
      plans: "Plans",
      "live-options": "Live Options Flow",
      "zdte": "0 DTE Flow",
      "task-demo": "Tasks",
      metrics: "User Metrics",
      subscriptions: "Subscriptions",
      "ai-metrics": "AI Metrics",
      attribution: "Attribution",
    }
    return titles[tabId] || "Dashboard"
  }

  // Admin-only tabs: redirect non-admins away if reached by direct URL.
  React.useEffect(() => {
    if (adminCheckLoading) return;
    if (!isAdmin && (tabId === "metrics" || tabId === "ai-metrics" || tabId === "subscriptions" || tabId === "attribution" || tabId === "task-demo")) {
      onTabChange("overview");
    }
  }, [isAdmin, adminCheckLoading, tabId, onTabChange]);

  // show trial-expired lock at dashboard
  const isTrialExpired = (() => {
    if (profile?.plan === "expired_trial") return true;
    
    // Check if plan is trial or trail (handling typo in DB)
    const isTrialPlan = profile?.plan === "trial" || profile?.plan === "trail";
    if (!isTrialPlan) return false;
    
    // Check trial_days_remaining if available
    if (profile?.trial_info?.trial_days_remaining !== undefined) {
      return profile.trial_info.trial_days_remaining <= 0;
    }
    
    // Otherwise check trial dates if available
    if (profile?.trial_ends_at) {
      const trialEndDate = new Date(profile.trial_ends_at);
      return new Date() > trialEndDate;
    }
    
    // Fallback to entitlements
    return entitlements?.trial_info?.expired || false;
  })();

  React.useEffect(() => {
    if (isToolboxPlan && (tabId === "watches" || tabId === "notifications")) {
      onTabChange("plans");
    }
  }, [isToolboxPlan, tabId, onTabChange]);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const renderTrialExpiredOverlay = () => {
    // Don't render overlay on plans page - users need to upgrade
    if (!isTrialExpired || tabId === "plans") return null;
    return <TrialExpiredDialog key={tabId} onUpgrade={onUpgrade} />;
  };

  // Update time every second
  React.useEffect(() => {
    function updateTime() {
      const now = new Date();
      const tz = getTimezone();
      const hour12 = getTimeFormat() === "12h";
      const formatted = now.toLocaleTimeString("en-US", {
        timeZone: tz,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12,
      });
      setEstTime(formatted);
    }
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const renderTabContent = () => {
    if (loading && tabId !== "overview") {
      return <PortalPageSkeleton tabId={tabId} />
    }

    switch (tabId) {
      case "overview":
        return (
          <ErrorBoundary section="dashboard.overview">
            <Overview
              profile={profile}
              entitlements={entitlements}
              onUpgrade={onUpgrade}
              api={api}
              loading={loading}
              onNewWatch={onNewWatch}
            />
          </ErrorBoundary>
        )
      case "watches":
        return (
          <ErrorBoundary section="dashboard.watches">
            <WatchesTab />
          </ErrorBoundary>
        )
      case "watches-create":
        return (
          <ErrorBoundary section="dashboard.watches-create">
            <WatchCreatePage />
          </ErrorBoundary>
        )
      case "trade-ideas":
        return (
          <ErrorBoundary section="dashboard.trade-ideas">
            <TradeIdeasPage api={api} />
          </ErrorBoundary>
        )
      case "trade-ideas-edition":
        return (
          <ErrorBoundary section="dashboard.trade-ideas-edition">
            <EditionPage api={api} asOf={editionAsOf ?? null} onSelectAsOf={onSelectEditionAsOf} />
          </ErrorBoundary>
        )
      case "trade-ideas-ledger":
        return (
          <ErrorBoundary section="dashboard.trade-ideas-ledger">
            <LedgerPage api={api} />
          </ErrorBoundary>
        )
      case "connector":
        return (
          <ErrorBoundary section="dashboard.connector">
            <ConnectorTab />
          </ErrorBoundary>
        )
      case "account":
        return (
          <ErrorBoundary section="dashboard.account">
            <AccountTab
              api={api}
              profile={profile}
              entitlements={entitlements}
              onUpgrade={onUpgrade}
            />
          </ErrorBoundary>
        )
      case "notifications":
        return (
          <ErrorBoundary section="dashboard.notifications">
            <NotificationsTab onUpgrade={onUpgrade} />
          </ErrorBoundary>
        )
      case "plans":
        return (
          <ErrorBoundary section="dashboard.plans">
            <PlansTab profile={profile} api={api} />
          </ErrorBoundary>
        )
      case "live-options":
        return (
          <ErrorBoundary section="dashboard.live-options">
            <LiveOptionsTab />
          </ErrorBoundary>
        )
      case "zdte":
        return (
          <ErrorBoundary section="dashboard.zdte">
            <ZeroDTETab />
          </ErrorBoundary>
        )
      case "task-demo":
        return (
          <ErrorBoundary section="dashboard.task-demo">
            <HelloWorldTask api={api} />
          </ErrorBoundary>
        )
      case "metrics":
        return (
          <ErrorBoundary section="dashboard.metrics">
            <UsageMetricsTab />
          </ErrorBoundary>
        )
      case "subscriptions":
        return (
          <ErrorBoundary section="dashboard.subscriptions">
            <SubscriptionsTab />
          </ErrorBoundary>
        )
      case "ai-metrics":
        return (
          <ErrorBoundary section="dashboard.ai-metrics">
            <AIMetricsTab />
          </ErrorBoundary>
        )
      case "attribution":
        return (
          <ErrorBoundary section="dashboard.attribution">
            <AttributionTab />
          </ErrorBoundary>
        )
      default:
        return (
          <ErrorBoundary section="dashboard.overview">
            <Overview
              profile={profile}
              entitlements={entitlements}
              onUpgrade={onUpgrade}
              api={api}
              loading={loading}
              onNewWatch={onNewWatch}
            />
          </ErrorBoundary>
        )
    }
  }

  if (isMobile) {
    return (
      <SidebarProvider>
        <MobileTabSync
          tabId={tabId}
          isMobile={isMobile}
          mobileMainRef={mobileMainRef}
        />
        <AppSidebar
          activeTab={tabId}
          onTabChange={onTabChange}
          profile={profile}
        />
        <div className="flex flex-col h-screen w-full">
          <header className="lg:hidden flex items-center justify-between h-14 px-4 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="h-5" />
              <div className="text-base font-semibold tracking-wide text-foreground flex-1 truncate">{getPageTitle()}</div>
            </div>
            {/* EST Clock on the right */}
            <span className="text-xs font-mono text-muted-foreground ml-2 whitespace-nowrap">{estTime} EST</span>
          </header>
          <main ref={mobileMainRef} className="relative flex-1 overflow-auto">
            <div className="p-4 bg-secondary min-h-[90vh]">{renderTabContent()}</div>
            {renderTrialExpiredOverlay()}
          </main>
        </div>
      </SidebarProvider>
    )
  }

  return (
    <SidebarProvider>
      <MobileTabSync
        tabId={tabId}
        isMobile={isMobile}
        mobileMainRef={mobileMainRef}
      />
      <AppSidebar
        activeTab={tabId}
        onTabChange={onTabChange}
        profile={profile}
      />
      {/* flex-1 min-w-0 (not w-full): w-full = 100% of the provider row, which
          stacked on top of the sidebar's width and forced ~240px of horizontal
          page scroll on every desktop portal page. */}
      <div className="hidden lg:flex flex-col min-h-[98vh] flex-1 min-w-0">
        {/* The Watch create flow pins its header and summary with position: sticky. A non-scrolling
            overflow-auto ancestor would become that sticky's scroll container and break pinning,
            so this route lets the document scroll instead. */}
        <main className={`relative flex-1 p-4 ${tabId === "watches-create" ? "" : "overflow-auto"}`}>
          <div className="p-8 bg-secondary rounded-lg min-h-[90vh]">{renderTabContent()}</div>
          {renderTrialExpiredOverlay()}
        </main>
      </div>
    </SidebarProvider>
  )
}
